/**
 * Client-side audio rendering using Web Audio API's OfflineAudioContext
 * Renders a mix of vocals + instrumental to a WAV blob
 */

interface RenderOptions {
  vocalsUrl: string
  instrumentalUrl: string
  vocalsVolume: number // 0-1
  instrumentalVolume: number // 0-1
  onProgress?: (progress: number) => void
}

interface RenderResult {
  blob: Blob
  duration: number
}

/**
 * Fetch and decode an audio file to AudioBuffer
 */
async function fetchAudioBuffer(url: string, audioContext: BaseAudioContext): Promise<AudioBuffer> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch audio: ${response.statusText}`)
  }
  const arrayBuffer = await response.arrayBuffer()
  return audioContext.decodeAudioData(arrayBuffer)
}

/**
 * Convert AudioBuffer to WAV format
 */
function audioBufferToWav(buffer: AudioBuffer): ArrayBuffer {
  const numChannels = buffer.numberOfChannels
  const sampleRate = buffer.sampleRate
  const format = 1 // PCM
  const bitDepth = 16

  const bytesPerSample = bitDepth / 8
  const blockAlign = numChannels * bytesPerSample
  const byteRate = sampleRate * blockAlign
  const dataSize = buffer.length * blockAlign
  const bufferSize = 44 + dataSize

  const arrayBuffer = new ArrayBuffer(bufferSize)
  const view = new DataView(arrayBuffer)

  // WAV header
  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i))
    }
  }

  writeString(0, "RIFF")
  view.setUint32(4, 36 + dataSize, true)
  writeString(8, "WAVE")
  writeString(12, "fmt ")
  view.setUint32(16, 16, true) // fmt chunk size
  view.setUint16(20, format, true)
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, byteRate, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bitDepth, true)
  writeString(36, "data")
  view.setUint32(40, dataSize, true)

  // Interleave channels and write samples
  const channels: Float32Array[] = []
  for (let i = 0; i < numChannels; i++) {
    channels.push(buffer.getChannelData(i))
  }

  let offset = 44
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, channels[ch][i]))
      const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7fff
      view.setInt16(offset, intSample, true)
      offset += 2
    }
  }

  return arrayBuffer
}

/**
 * Render a mashup mix to a WAV blob using OfflineAudioContext
 * This renders faster than real-time
 */
export async function renderMashup(options: RenderOptions): Promise<RenderResult> {
  const { vocalsUrl, instrumentalUrl, vocalsVolume, instrumentalVolume, onProgress } = options

  onProgress?.(5)

  // First, fetch both audio files using a temporary context
  const tempContext = new AudioContext()

  try {
    onProgress?.(10)

    const [vocalsBuffer, instrumentalBuffer] = await Promise.all([
      fetchAudioBuffer(vocalsUrl, tempContext),
      fetchAudioBuffer(instrumentalUrl, tempContext),
    ])

    onProgress?.(30)

    // Use the longer duration
    const duration = Math.max(vocalsBuffer.duration, instrumentalBuffer.duration)
    const sampleRate = 44100

    // Create offline context for rendering
    const offlineContext = new OfflineAudioContext(
      2, // stereo
      Math.ceil(duration * sampleRate),
      sampleRate,
    )

    // Create gain nodes for volume control
    const vocalsGain = offlineContext.createGain()
    vocalsGain.gain.value = vocalsVolume

    const instrumentalGain = offlineContext.createGain()
    instrumentalGain.gain.value = instrumentalVolume

    // Create source nodes
    const vocalsSource = offlineContext.createBufferSource()
    vocalsSource.buffer = vocalsBuffer
    vocalsSource.connect(vocalsGain)
    vocalsGain.connect(offlineContext.destination)

    const instrumentalSource = offlineContext.createBufferSource()
    instrumentalSource.buffer = instrumentalBuffer
    instrumentalSource.connect(instrumentalGain)
    instrumentalGain.connect(offlineContext.destination)

    // Start both sources
    vocalsSource.start(0)
    instrumentalSource.start(0)

    onProgress?.(40)

    // Render
    const renderedBuffer = await offlineContext.startRendering()

    onProgress?.(80)

    // Convert to WAV
    const wavData = audioBufferToWav(renderedBuffer)
    const blob = new Blob([wavData], { type: "audio/wav" })

    onProgress?.(100)

    return {
      blob,
      duration,
    }
  } finally {
    await tempContext.close()
  }
}

/**
 * Create a download URL for a blob
 */
export function createDownloadUrl(blob: Blob): string {
  return URL.createObjectURL(blob)
}

/**
 * Trigger a download of a blob
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = createDownloadUrl(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
